import {
  Component,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  AfterViewInit,
  TrackByFunction,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CalendarDay,
  CalendarModalOptions,
  CalendarMonth,
  CalendarOriginal,
  PickMode,
  SelectMode,
} from '../calendar.model';
import { defaults, pickModes } from '../config';
import * as moment from 'moment';
import { CalendarService } from '../services/calendar.service';

export const MONTH_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MonthComponent),
  multi: true,
};

@Component({
  selector: 'ion-calendar-month',
  providers: [MONTH_VALUE_ACCESSOR],
  styleUrls: ['./month.component.scss'],
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    '[class.component-mode]': 'componentMode',
  },
  template: `
    <div [class]="color">
      <ng-template [ngIf]="!_isRange" [ngIfElse]="rangeBox">
        <div class="days-box">
          <ng-template
            ngFor
            let-day
            [ngForOf]="month.days"
            [ngForTrackBy]="trackByTime"
          >
            <div class="days">
              <ng-container *ngIf="day">
                <button
                  type="button"
                  [class]="'days-btn ' + day.cssClass"
                  [class.today]="day.isToday"
                  (click)="onSelected(day)"
                  [class.marked]="day.marked"
                  [class.last-month-day]="day.isLastMonth"
                  [class.next-month-day]="day.isNextMonth"
                  [class.on-selected]="isSelected(day.time)"
                  [disabled]="day.disable"
                  [attr.aria-label]="getDayLabel(day) | date: DAY_DATE_FORMAT"
                >
                  <p>{{ day.title }}</p>
                  <small *ngIf="day.subTitle">{{ day?.subTitle }}</small>
                  <div *ngIf="day.isScheduled" class="scheduled"></div>
                </button>
              </ng-container>
            </div>
          </ng-template>
        </div>
      </ng-template>

      <ng-template #rangeBox>
        <div class="days-box">
          <ng-template
            ngFor
            let-day
            [ngForOf]="month.days"
            [ngForTrackBy]="trackByTime"
          >
            <div
              class="days"
              [class.startSelection]="isStartSelection(day)"
              [class.endSelection]="isEndSelection(day)"
              [class.is-first-wrap]="day?.isFirst"
              [class.is-last-wrap]="day?.isLast"
              [class.between]="isBetween(day)"
            >
              <ng-container *ngIf="day">
                <button
                  type="button"
                  [class]="'days-btn ' + day.cssClass"
                  [class.today]="day.isToday"
                  (click)="onSelected(day)"
                  [class.marked]="day.marked"
                  [class.last-month-day]="day.isLastMonth"
                  [class.next-month-day]="day.isNextMonth"
                  [class.is-first]="day.isFirst"
                  [class.is-last]="day.isLast"
                  [class.on-selected]="isSelected(day.time)"
                  [disabled]="day.disable"
                >
                  <p>{{ day.title }}</p>
                  <small *ngIf="day.subTitle">{{ day?.subTitle }}</small>
                </button>
              </ng-container>
            </div>
          </ng-template>
        </div>
      </ng-template>
    </div>
  `,
})
export class MonthComponent implements ControlValueAccessor, AfterViewInit {
  _mode: SelectMode = undefined;
  @Input()
  set mode(val: SelectMode) {
    this._mode = val;
  }
  get mode() {
    return this._mode;
  }
  @Input() opt: CalendarModalOptions;
  @Input() componentMode = false;
  @Input()
  month: CalendarMonth;
  @Input()
  pickMode: PickMode;
  @Input()
  isSaveHistory: boolean;
  @Input()
  id: any;
  @Input()
  readonly = false;
  @Input()
  color: string = defaults.COLOR;

  @Output()
  change: EventEmitter<CalendarDay[]> = new EventEmitter();
  @Output()
  select: EventEmitter<CalendarDay> = new EventEmitter();
  @Output()
  selectStart: EventEmitter<CalendarDay> = new EventEmitter();
  @Output()
  selectEnd: EventEmitter<CalendarDay> = new EventEmitter();

  _date: Array<CalendarDay | null> = [null, null];
  _isInit = false;
  _onChanged: Function;
  _onTouched: Function;

  readonly DAY_DATE_FORMAT = 'MMMM dd, yyyy';

  get _isRange(): boolean {
    return (
      this.pickMode === pickModes.RANGE ||
      this.pickMode === pickModes.SINGLEWEEK ||
      this.mode === 'week'
    );
  }

  constructor(public ref: ChangeDetectorRef, public calSvc: CalendarService) {}

  ngAfterViewInit(): void {
    this._isInit = true;
  }

  get value() {
    return this._date;
  }

  writeValue(obj: any): void {
    if (Array.isArray(obj)) {
      this._date = obj;
    }
  }

  registerOnChange(fn: any): void {
    this._onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  trackByTime: TrackByFunction<any> = (
    index: number,
    item: CalendarOriginal
  ) => {
    return item ? item.time : index;
  };

  isEndSelection(day?: CalendarDay): boolean {
    if (!day) return false;
    if (
      (this.pickMode !== pickModes.RANGE &&
        this.pickMode !== pickModes.SINGLEWEEK &&
        this.mode !== 'week') ||
      !this._isInit ||
      this._date[1] === null
    ) {
      return false;
    }

    return this._date[1].time === day.time;
  }

  getDayLabel(day: CalendarDay) {
    return new Date(day.time);
  }

  isBetween(day?: CalendarDay): boolean {
    if (!day) return false;

    if (
      (this.pickMode !== pickModes.RANGE &&
        this.pickMode !== pickModes.SINGLEWEEK &&
        this.mode !== 'week') ||
      !this._isInit
    ) {
      return false;
    }

    if (this._date[0] === null || this._date[1] === null) {
      return false;
    }

    const start = this._date[0].time;
    const end = this._date[1].time;

    return day.time < end && day.time > start;
  }

  isStartSelection(day?: CalendarDay): boolean {
    if (!day) return false;
    if (
      (this.pickMode !== pickModes.RANGE &&
        this.pickMode !== pickModes.SINGLEWEEK &&
        this.mode !== 'week') ||
      !this._isInit ||
      this._date[0] === null
    ) {
      return false;
    }

    return this._date[0].time === day.time && this._date[1] !== null;
  }

  isSelected(time: number): boolean {
    if (Array.isArray(this._date)) {
      if (this.pickMode !== pickModes.MULTI) {
        if (this._date[0] !== null) {
          return time === this._date[0].time;
        }

        if (this._date[1] !== null) {
          return time === this._date[1].time;
        }
      } else {
        return (
          this._date.findIndex((e) => e !== null && e.time === time) !== -1
        );
      }
    } else {
      return false;
    }
  }

  onSelected(item: CalendarDay): void {
    if (this.readonly) return;
    item.selected = true;
    this.select.emit(item);
    if (this.pickMode === pickModes.SINGLE) {
      if (this.mode === 'week') {
        const wd = moment(item.time).weekday();
        let tgTime = item.time;
        let fir = moment(tgTime)
          .subtract(wd - (this.opt.firstDay || 0), 'days')
          .valueOf();
        if (fir > tgTime) {
          tgTime = moment(tgTime).subtract(1, 'week').valueOf();

          fir = moment(tgTime)
            .subtract(wd - (this.opt.firstDay || 0), 'days')
            .valueOf();
        }
        const lat = moment(tgTime)
          .add(6 + (this.opt.firstDay || 0) - wd, 'days')
          .valueOf();
        this._date[0] = this.calSvc.createCalendarDay(fir, this.opt);
        this._date[1] = this.calSvc.createCalendarDay(lat, this.opt);
        this.change.emit(this._date);
      } else {
        this._date[0] = item;
        this.change.emit(this._date);
      }
      return;
    }

    if (this.pickMode === pickModes.SINGLEWEEK) {
      const wd = moment(item.time).weekday();
      let tgTime = item.time;
      let fir = moment(tgTime)
        .subtract(wd - (this.opt.firstDay || 0), 'days')
        .valueOf();
      if (fir > tgTime) {
        tgTime = moment(tgTime).subtract(1, 'week').valueOf();

        fir = moment(tgTime)
          .subtract(wd - (this.opt.firstDay || 0), 'days')
          .valueOf();
      }
      const lat = moment(tgTime)
        .add(6 + (this.opt.firstDay || 0) - wd, 'days')
        .valueOf();
      this._date[0] = this.calSvc.createCalendarDay(fir, this.opt);
      this._date[1] = this.calSvc.createCalendarDay(lat, this.opt);
      this.change.emit(this._date);

      return;
    }

    if (this.pickMode === pickModes.RANGE) {
      if (this._date[0] === null) {
        this._date[0] = item;
        this.selectStart.emit(item);
      } else if (this._date[1] === null) {
        if (this._date[0].time < item.time) {
          this._date[1] = item;
          this.selectEnd.emit(item);
        } else {
          this._date[1] = this._date[0];
          this.selectEnd.emit(this._date[0]);
          this._date[0] = item;
          this.selectStart.emit(item);
        }
      } else if (this._date[0].time > item.time) {
        this._date[0] = item;
        this.selectStart.emit(item);
      } else if (this._date[1].time < item.time) {
        this._date[1] = item;
        this.selectEnd.emit(item);
      } else {
        this._date[0] = item;
        this.selectStart.emit(item);
        this._date[1] = null;
      }

      this.change.emit(this._date);
      return;
    }

    if (this.pickMode === pickModes.MULTI) {
      const index = this._date.findIndex(
        (e) => e !== null && e.time === item.time
      );

      if (index === -1) {
        this._date.push(item);
      } else {
        this._date.splice(index, 1);
      }
      this.change.emit(this._date.filter((e) => e !== null));
    }
  }
}
